/**
 * Query result caching service with TTL-based expiration
 * Caches query results based on date range, filter complexity, and query type
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  key: string;
}

interface CacheConfig {
  defaultTTL: number; // Default TTL in milliseconds
  maxSize: number; // Maximum number of cache entries
}

class QueryCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutes default
      maxSize: 1000, // Maximum 1000 entries
      ...config
    };

    // Clean up expired entries every minute
    setInterval(() => this.cleanup(), 60 * 1000);
  }

  /**
   * Generate a cache key from query parameters
   */
  private generateKey(
    queryName: string,
    params: any,
    options?: { includeDateRange?: boolean; includeFilters?: boolean }
  ): string {
    const { includeDateRange = true, includeFilters = true } = options || {};
    
    const keyParts = [queryName];
    
    if (includeDateRange && params.fromDate && params.toDate) {
      // Round dates to nearest hour for better cache hits
      const from = new Date(params.fromDate);
      const to = new Date(params.toDate);
      from.setMinutes(0, 0, 0);
      to.setMinutes(0, 0, 0);
      keyParts.push(`date:${from.toISOString()}-${to.toISOString()}`);
    }
    
    if (includeFilters) {
      if (params.filterAst) {
        keyParts.push(`filterAst:${JSON.stringify(params.filterAst)}`);
      } else if (params.filterExpression) {
        keyParts.push(`filterExpression:${params.filterExpression}`);
      }
    }
    
    // Include other relevant params
    if (params.productIds != null) keyParts.push(`productIds:${params.productIds}`);
    if (params.limit) keyParts.push(`limit:${params.limit}`);
    if (params.offset) keyParts.push(`offset:${params.offset}`);
    if (params.page) keyParts.push(`page:${params.page}`);
    if (params.pageSize) keyParts.push(`pageSize:${params.pageSize}`);
    
    return keyParts.join('|');
  }

  /**
   * Calculate TTL based on query type and date range
   */
  private calculateTTL(queryName: string, params: any): number {
    // Shorter TTL for recent dates (data changes frequently)
    if (params.fromDate && params.toDate) {
      const to = new Date(params.toDate);
      const now = new Date();
      const daysAgo = (now.getTime() - to.getTime()) / (1000 * 60 * 60 * 24);
      
      // Recent data (< 7 days): 1 minute TTL
      if (daysAgo < 7) {
        return 60 * 1000;
      }
      // Recent data (< 30 days): 5 minutes TTL
      if (daysAgo < 30) {
        return 5 * 60 * 1000;
      }
      // Older data: 15 minutes TTL
      return 15 * 60 * 1000;
    }
    
    // Query-specific TTLs
    const queryTTLs: Record<string, number> = {
      'getScalarAttributeCount': 10 * 60 * 1000, // 10 minutes
      'getArrayAttributeCount': 10 * 60 * 1000, // 10 minutes
      'getIndicatorTypeCountFilter': 5 * 60 * 1000, // 5 minutes
      'getFilteredProductCount': 2 * 60 * 1000, // 2 minutes
      'getFilteredProductIdsWithCount': 2 * 60 * 1000, // 2 minutes
      'getProducts': 5 * 60 * 1000, // 5 minutes
      'getTimeSeriesByDateRange': 10 * 60 * 1000, // 10 minutes
      'searchIOC': 1 * 60 * 1000, // 1 minute (IOC data changes frequently)
    };
    
    return queryTTLs[queryName] || this.config.defaultTTL;
  }

  /**
   * Get cached result if available and not expired
   */
  get<T>(
    queryName: string,
    params: any,
    options?: { includeDateRange?: boolean; includeFilters?: boolean }
  ): T | null {
    const key = this.generateKey(queryName, params, options);
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    const now = Date.now();
    const age = now - entry.timestamp;
    
    if (age > entry.ttl) {
      // Entry expired
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  /**
   * Store a result in the cache
   */
  set<T>(
    queryName: string,
    params: any,
    data: T,
    options?: { includeDateRange?: boolean; includeFilters?: boolean; customTTL?: number }
  ): void {
    // Enforce max size by removing oldest entries
    if (this.cache.size >= this.config.maxSize) {
      const oldestKey = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
      this.cache.delete(oldestKey);
    }
    
    const key = this.generateKey(queryName, params, options);
    const ttl = options?.customTTL || this.calculateTTL(queryName, params);
    
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
      key
    });
  }

  /**
   * Invalidate cache entries matching a pattern
   */
  invalidate(queryName?: string, params?: any): void {
    if (!queryName) {
      // Clear all cache
      this.cache.clear();
      return;
    }
    
    if (!params) {
      // Clear all entries for this query name
      const keysToDelete: string[] = [];
      this.cache.forEach((entry, key) => {
        if (key.startsWith(queryName + '|')) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.cache.delete(key));
      return;
    }
    
    // Clear specific entry
    const key = this.generateKey(queryName, params);
    this.cache.delete(key);
  }

  /**
   * Clear expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.cache.forEach((entry, key) => {
      const age = now - entry.timestamp;
      if (age > entry.ttl) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.cache.delete(key));
    
    if (keysToDelete.length > 0) {
      console.debug(`QueryCache: Cleaned up ${keysToDelete.length} expired entries`);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    entries: Array<{ key: string; age: number; ttl: number }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: now - entry.timestamp,
      ttl: entry.ttl
    }));
    
    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      entries
    };
  }
}

// Export singleton instance
export const queryCache = new QueryCache();

// Export types for use in other files
export type { CacheConfig };

